// Decision engine + notice template for the "FTB Automation" outage-pipeline
// scenario, modeled on "FTB Automation Information.docx" (the ICE NOC
// site-outage FTB — "flash traffic bulletin" — notification process). This
// is demo logic for the OutagePipelinePage fan-out, not a real integration:
// SITE_DIRECTORY stands in for the real SNOW Location table and the
// SharePoint "Processing Centers and Surge Sites" workbook the docx
// describes, and FTB_DEMO_NOTIFICATION stands in for a real NOC Teams-chat
// pull — the docx's own example ticket (INC4200637, site RFZ) reused
// verbatim.

export interface FtbSite {
  code: string;
  name: string;
  cityState: string;
  essential: boolean;
  onSurgeList: boolean;
}

// Del Rio TX Co-Location (TCL), Dilley Processing Center (DIL), and El Valle
// Center (EVC) are the docx's own "Example site table" — real names/codes
// as given there. RFZ is this scenario's incident site, from the docx's
// NOC-notification example, flagged on the surge list so the walkthrough
// exercises that catch-all rule. PTN/EOW are the two sites the docx calls
// out by name in the FTB requirements table; the remaining codes are filler
// so the essential/10-site count rules have real data to run against.
export const SITE_DIRECTORY: FtbSite[] = [
  { code: 'TCL', name: 'Del Rio TX Co-Location', cityState: 'Del Rio, TX', essential: false, onSurgeList: true },
  { code: 'DIL', name: 'Dilley Processing Center', cityState: 'Dilley, TX', essential: true, onSurgeList: true },
  { code: 'EVC', name: 'El Valle Center', cityState: 'Raymondville, TX', essential: false, onSurgeList: true },
  { code: 'RFZ', name: 'Site RFZ', cityState: 'TX', essential: false, onSurgeList: true },
  { code: 'PTN', name: 'Site PTN', cityState: 'TX', essential: true, onSurgeList: false },
  { code: 'EOW', name: 'Site EOW', cityState: 'TX', essential: true, onSurgeList: false },
  { code: 'BRV', name: 'Site BRV', cityState: 'TX', essential: false, onSurgeList: false },
  { code: 'LRD', name: 'Site LRD', cityState: 'TX', essential: true, onSurgeList: false },
  { code: 'MRC', name: 'Site MRC', cityState: 'TX', essential: true, onSurgeList: false },
  { code: 'WCH', name: 'Site WCH', cityState: 'TX', essential: true, onSurgeList: false },
  { code: 'HLN', name: 'Site HLN', cityState: 'TX', essential: false, onSurgeList: false },
];

export function lookupSite(code: string): FtbSite | undefined {
  const upper = code.toUpperCase();
  return SITE_DIRECTORY.find((s) => s.code === upper);
}

export interface FtbRuleCheck {
  rule: string;
  matched: boolean;
  detail: string;
}

export interface FtbDecision {
  required: boolean;
  reason: string;
  trail: FtbRuleCheck[];
}

// Evaluates the docx's "FTB requirements for Site Outages" table. The two
// named sites (PTN/EOW) and the surge-site list are unconditional "Yes"
// exceptions in the docx — a PTN-only outage is a single impacted site,
// which the plain sites-impacted count would otherwise call "No" — so
// those checks run first and short-circuit the count-based rules below.
export function decideFtb(siteCodes: string[]): FtbDecision {
  const codes = siteCodes.map((c) => c.toUpperCase());
  const sites = codes.map((c) => lookupSite(c)).filter((s): s is FtbSite => !!s);
  const trail: FtbRuleCheck[] = [];

  const ptn = codes.includes('PTN');
  trail.push({ rule: 'Site outage at site PTN', matched: ptn, detail: ptn ? 'PTN is impacted.' : 'PTN not impacted.' });
  if (ptn) return { required: true, reason: 'Site outage at site PTN', trail };

  const eow = codes.includes('EOW');
  trail.push({ rule: 'Site outage at site EOW', matched: eow, detail: eow ? 'EOW is impacted.' : 'EOW not impacted.' });
  if (eow) return { required: true, reason: 'Site outage at site EOW', trail };

  const surgeSites = sites.filter((s) => s.onSurgeList);
  const onSurge = surgeSites.length > 0;
  trail.push({
    rule: 'Any number of sites — on Processing Centers and Surge Sites list',
    matched: onSurge,
    detail: onSurge
      ? `${surgeSites.map((s) => s.code).join(', ')} on the Processing Centers and Surge Sites list.`
      : 'No impacted site is on the Processing Centers and Surge Sites list.',
  });
  if (onSurge) return { required: true, reason: 'On the Processing Centers and Surge Sites list', trail };

  const total = codes.length;
  const essentialCount = sites.filter((s) => s.essential).length;

  const lessThan3 = total < 3;
  trail.push({ rule: 'Less than 3 sites impacted', matched: lessThan3, detail: `${total} site(s) impacted.` });
  if (lessThan3) return { required: false, reason: 'Less than 3 sites impacted', trail };

  const essential3plus = essentialCount >= 3;
  trail.push({
    rule: '3 or more Essential sites impacted',
    matched: essential3plus,
    detail: `${essentialCount} essential site(s) impacted.`,
  });
  if (essential3plus) return { required: true, reason: '3 or more Essential sites impacted', trail };

  const total10plus = total >= 10;
  trail.push({ rule: '10 or more sites impacted', matched: total10plus, detail: `${total} site(s) impacted.` });
  if (total10plus) return { required: true, reason: '10 or more sites impacted', trail };

  trail.push({
    rule: '3 or more sites impacted, fewer than 3 essential, fewer than 10 total',
    matched: true,
    detail: `${total} site(s) impacted, ${essentialCount} essential.`,
  });
  return {
    required: false,
    reason: '3 or more sites impacted, fewer than 3 essential and fewer than 10 total',
    trail,
  };
}

// Converts a NOC-style "1211 ET 8/18/2026" 24-hour timestamp to the
// 12-hour "08/18/26 at 12:11 PM ET" format the docx's "Include in
// Automation" note asks for. Falls back to the raw string on anything that
// doesn't match — better an unconverted timestamp shown than a blank field.
export function formatNocTimestamp(raw: string): string {
  const m = raw.match(/^(\d{3,4})\s*ET\s*(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return raw;
  const [, hhmm, mo, da, yr] = m;
  const padded = hhmm.padStart(4, '0');
  const hour = parseInt(padded.slice(0, 2), 10);
  const minute = padded.slice(2);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const mm = mo.padStart(2, '0');
  const dd = da.padStart(2, '0');
  const yy = yr.slice(2);
  return `${mm}/${dd}/${yy} at ${hour12}:${minute} ${ampm} ET`;
}

export interface NocPull {
  ticket: string;
  siteCode: string;
  type: string;
  device: string;
  initial: string;
  final: string | null;
  poc: string;
  status: string;
}

export interface FtbNotice {
  whatIsBroken: string;
  timeframe: string;
  qtyServiceDeskTickets: number;
  operationalImpact: string;
  mitigations: string;
}

// Fills the docx's "FTB Template" from a NOC pull + the site's SNOW-style
// location name.
export function buildFtbNotice({
  noc,
  siteName,
  qtyServiceDeskTickets = 2,
}: {
  noc: NocPull;
  siteName: string;
  qtyServiceDeskTickets?: number;
}): FtbNotice {
  const initialFmt = formatNocTimestamp(noc.initial);
  const isOngoing = !noc.final || noc.final.toUpperCase() === 'TBD';
  const timeframe = isOngoing
    ? `${initialFmt} — currently ongoing`
    : `${initialFmt} through ${formatNocTimestamp(noc.final as string)}`;

  return {
    whatIsBroken: `${siteName} is experiencing a loss of ${noc.type} network connectivity due to ${noc.status.toLowerCase()}.`,
    timeframe,
    qtyServiceDeskTickets,
    operationalImpact: `Users at ${noc.siteCode} are unable to access the network. Users can access the network through hotspot.`,
    mitigations: `${noc.status} — to be updated by IC.`,
  };
}

export const FTB_TICKET_ID = 'INC4200637';

// The docx's own "Pull from Notification: (example)" block, reused as this
// scenario's demo NOC pull. major_incident has no columns for device/POC/
// initial-time, so these fields live here rather than on the incident row.
export const FTB_DEMO_NOTIFICATION: NocPull = {
  ticket: FTB_TICKET_ID,
  siteCode: 'RFZ',
  type: 'WAN',
  device: 'rrfzE01',
  initial: '1211 ET 8/18/2026',
  final: 'TBD',
  poc: 'Don Bassett (602) 291-9114',
  status: 'Opening ticket with Telco',
};
